/** 육상·시나리오 위험 임계값 (시스템 → 시설 → CCTV 승계). 익사 motion과 분리. */

export interface RiskLandThresholds {
  /** 일반 배회 정지(초) */
  loitering_stillness_sec: number;
  /** 특별관리구역 체류(초) */
  special_zone_dwell_sec: number;
  /** 밀집 인원 임계 */
  crowd_density_count: number;
  /** 육상 알림 쿨다운(초) */
  land_alert_cooldown_sec: number;
  /** 미끄러짐 후 정지(초) — 고급 */
  slip_still_sec: number;
  /** 쓰러짐 체류(초) — 고급 */
  fallen_still_sec: number;
}

export type RiskLandThresholdKey = keyof RiskLandThresholds;

export const DEFAULT_RISK_LAND_THRESHOLDS: RiskLandThresholds = {
  loitering_stillness_sec: 18,
  special_zone_dwell_sec: 8,
  crowd_density_count: 25,
  land_alert_cooldown_sec: 25,
  slip_still_sec: 1.2,
  fallen_still_sec: 2.5,
};

export const RISK_LAND_THRESHOLD_META: Record<
  RiskLandThresholdKey,
  { label: string; helper: string; min: number; max: number; step: number; advanced?: boolean }
> = {
  loitering_stillness_sec: {
    label: '배회 정지(초)',
    helper: '같은 자리에서 이 시간 이상이면 배회 위험',
    min: 3,
    max: 600,
    step: 1,
  },
  special_zone_dwell_sec: {
    label: '특별관리구역 체류(초)',
    helper: '빨간 박스 안 체류 시 더 빠른 위험 판정',
    min: 3,
    max: 600,
    step: 1,
  },
  crowd_density_count: {
    label: '밀집 인원수',
    helper: '한 화면에 이 인원 이상이면 밀집 위험',
    min: 2,
    max: 500,
    step: 1,
  },
  land_alert_cooldown_sec: {
    label: '육상 알림 쿨다운(초)',
    helper: '동일 대상 재알림 억제',
    min: 5,
    max: 600,
    step: 1,
  },
  slip_still_sec: {
    label: '미끄러짐 후 정지(초)',
    helper: '고급 · 전도 판정',
    min: 0.3,
    max: 60,
    step: 0.1,
    advanced: true,
  },
  fallen_still_sec: {
    label: '쓰러짐 체류(초)',
    helper: '고급 · 쓰러짐 판정',
    min: 0.5,
    max: 120,
    step: 0.1,
    advanced: true,
  },
};

function clamp(key: RiskLandThresholdKey, n: number): number {
  const m = RISK_LAND_THRESHOLD_META[key];
  return Math.min(m.max, Math.max(m.min, n));
}

/** 부분 오버라이드만 추출 (빈 값·동일 기본값 제외는 호출측에서) */
export function sanitizeLandThresholdsPartial(
  partial?: Partial<RiskLandThresholds> | Record<string, unknown> | null
): Partial<RiskLandThresholds> {
  if (!partial || typeof partial !== 'object') return {};
  const out: Partial<RiskLandThresholds> = {};
  (Object.keys(DEFAULT_RISK_LAND_THRESHOLDS) as RiskLandThresholdKey[]).forEach((key) => {
    if (!(key in partial)) return;
    const raw = (partial as Record<string, unknown>)[key];
    if (raw === null || raw === undefined || raw === '') return;
    const n = Number(raw);
    if (!Number.isFinite(n)) return;
    out[key] = clamp(key, n);
  });
  return out;
}

export function mergeRiskLandThresholds(
  ...layers: Array<Partial<RiskLandThresholds> | Record<string, unknown> | null | undefined>
): RiskLandThresholds {
  let out: RiskLandThresholds = { ...DEFAULT_RISK_LAND_THRESHOLDS };
  for (const layer of layers) {
    const part = sanitizeLandThresholdsPartial(layer);
    out = { ...out, ...part };
  }
  return out;
}

/** 시설 저장용: 시스템과 다른 키만 남김 */
export function diffLandThresholdsFromBase(
  effective: RiskLandThresholds,
  base: RiskLandThresholds
): Partial<RiskLandThresholds> | null {
  const diff: Partial<RiskLandThresholds> = {};
  (Object.keys(DEFAULT_RISK_LAND_THRESHOLDS) as RiskLandThresholdKey[]).forEach((key) => {
    if (Number(effective[key]) !== Number(base[key])) {
      diff[key] = effective[key];
    }
  });
  return Object.keys(diff).length ? diff : null;
}
